import he from 'he';
import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import {
  getAvailablePriceTypes,
  TAG_PRINTING_TEMPLATE_QUERY,
} from '@/components/tag-printing-template-selection-v2';
import { gql, useMutation } from '@apollo/client';
import { KeyboardAvoidingView, Platform, Text, View } from 'react-native';
import { List } from '@/components/list';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { ModalContainer } from '@/components/modal-container';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { roundMicrosToNearestWholeCent } from '@/utils/money';
import { StackScreenProps } from '@react-navigation/stack';
import { TAG_PRINT_BATCH_ROUTES } from '@/navigators/routes';
import {
  TagPrint_UpdateRetailPriceMutation,
  TagPrint_UpdateRetailPriceMutationVariables,
  TagPrintingTemplateQuery,
  TagPrintingTemplateQueryVariables,
} from '@/types';
import { TagPrintBatchStackParams } from '@/navigators/types';
import { TagPrintingTemplateCustomPriceField } from '@/components/tag-printing-template-custom-price-field';
import {
  TagPrintingTemplateMetadata,
  UserDefinedMetadata,
} from '@/components/tag-printing-types';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import {
  useOfflineTagBatchPrint,
  useOfflineVisibleVariantPacks,
} from '@/hooks/use-offline-data';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const UPDATE_VARIANT_PACK_RETAIL_PRICE = gql`
  mutation TagPrint_UpdateRetailPrice(
    $restaurantUUID: uuid!
    $retailPriceInMicros: bigint
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    upsertVariantPackRestaurantRetailPrice(
      restaurant_id: $restaurantUUID
      retail_price_in_micros: $retailPriceInMicros
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      success
    }
  }
`;

type Props = StackScreenProps<
  TagPrintBatchStackParams,
  TAG_PRINT_BATCH_ROUTES.Item
>;

export const TagPrintBatchItemScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    batchItemPriceInMicros,
    displayName,
    isInstantPrint,
    imageURLs,
    offlinePrices,
    onPrintAgain,
    onRetailPriceUpdate,
    printTagBatchUUID,
    templateUUID,
    variantPackUUID,
  } = route.params;
  const [customPriceInMicros, setCustomPriceInMicros] = useState<number | null>(
    batchItemPriceInMicros ?? null,
  );
  const [priceHydrated, setPriceHydrated] = useState(false);

  const { hidePlaceholderImages } = useItemDisplayOptions();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();

  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const isOfflineModeActive = useOfflineModeActive();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const [styles] = useStyles();

  const retailPriceInMicros =
    typeof customPriceInMicros === 'number'
      ? roundMicrosToNearestWholeCent(customPriceInMicros)
      : undefined;

  const { data: onlineTagPrintingTemplatesData, loading } = useWrappedQuery<
    TagPrintingTemplateQuery,
    TagPrintingTemplateQueryVariables
  >(TAG_PRINTING_TEMPLATE_QUERY, {
    fetchPolicy: 'no-cache',
    skip: isOfflineModeActive,
    variables: {
      restaurantUUID,
      supplierUUID,
      variantPackUUIDs: [variantPackUUID],
    },
  });

  const [updateRetailPrice] = useMutation<
    TagPrint_UpdateRetailPriceMutation,
    TagPrint_UpdateRetailPriceMutationVariables
  >(UPDATE_VARIANT_PACK_RETAIL_PRICE, {
    variables: {
      restaurantUUID,
      retailPriceInMicros,
      supplierUUID,
      variantPackUUID,
    },
  });

  const data = isOfflineModeActive ? null : onlineTagPrintingTemplatesData;

  const { tagBatchPrintData: offlineTagBatchPrintData } =
    useOfflineTagBatchPrint(restaurantUUID, supplierUUID);

  const { data: offlineVisibleVariantPackData } = useOfflineVisibleVariantPacks(
    restaurantUUID,
    supplierUUID,
  );

  // get the prices for this variant pack from the offline storage
  const offlineVariantPackPrices =
    offlineTagBatchPrintData?.prices?.[variantPackUUID];

  // get the offline print template for this batch
  const selectedOfflinePrinterTextTemplate =
    offlineTagBatchPrintData?.templates?.[templateUUID];

  // get data for this variant pack from offlineVisibleVariantPackData
  const offlineVisibleVariantPack =
    offlineVisibleVariantPackData?.visible_variant_packs.find(
      (vp) => vp.variant_pack?.uuid === variantPackUUID,
    )?.variant_pack;
  const adaptedOfflineVisibleVariantPack = offlineVisibleVariantPack
    ? adaptVariantPackToItemListOption(offlineVisibleVariantPack)
    : null;

  const variantPack = data?.visible_variant_packs?.[0]?.variant_pack;
  const variantPackOverrides =
    variantPack?.restaurant_variant_pack_overrides?.[0] || null;

  useEffect(() => {
    if (!loading) {
      const userDefinedMetadata =
        variantPackOverrides?.user_defined_metadata as
          | UserDefinedMetadata
          | undefined;
      const initialRetailPriceInMicros =
        userDefinedMetadata?.list_prices?.retail_price_in_micros;
      if (typeof initialRetailPriceInMicros === 'number') {
        setCustomPriceInMicros(initialRetailPriceInMicros);
      }

      setPriceHydrated(true);
    }
  }, [loading, variantPackOverrides]);

  const templateData = useMemo(() => {
    return (data?.getPrintTagTemplatesForRestaurant || []).find(
      (response) => response.print_tag_template?.uuid === templateUUID,
    );
  }, [data?.getPrintTagTemplatesForRestaurant, templateUUID]);

  const templateMetadata: TagPrintingTemplateMetadata | undefined =
    isOfflineModeActive
      ? selectedOfflinePrinterTextTemplate?.metadata
      : templateData?.print_tag_template?.metadata;

  const retailPriceEnabled = templateMetadata?.features?.retail_price_enabled;

  useComponentFocus({
    onBeforeRemove: useCallback(async () => {
      if (retailPriceEnabled) {
        if (isOfflineModeActive) {
          onRetailPriceUpdate(retailPriceInMicros);
        } else {
          try {
            const response = await updateRetailPrice();
            if (
              !response.data?.upsertVariantPackRestaurantRetailPrice.success
            ) {
              throw new Error('Failed to update retail price');
            }

            onRetailPriceUpdate(retailPriceInMicros);
          } catch {
            showErrorToast();
          }
        }
      }
    }, [
      isOfflineModeActive,
      onRetailPriceUpdate,
      retailPriceEnabled,
      retailPriceInMicros,
      updateRetailPrice,
      showErrorToast,
    ]),
  });

  const previewFields = useMemo(() => {
    if (isOfflineModeActive) {
      const externalID =
        adaptedOfflineVisibleVariantPack?.metadata?.externalID ?? '';
      const packUnit = adaptedOfflineVisibleVariantPack?.metadata?.packUnit;
      return [
        {
          title: i18n.t('item_code'),
          value: externalID,
        },
        {
          title: i18n.t('pack_unit'),
          value: packUnit ? (LOCALIZED_PACK_UNITS[packUnit] ?? packUnit) : '',
        },
      ];
    }
    return (templateData?.preview_display_fields || []).map((field) => ({
      title: field.key,
      value: field.value,
    }));
  }, [
    adaptedOfflineVisibleVariantPack,
    isOfflineModeActive,
    templateData?.preview_display_fields,
  ]);

  const maxPreviewFieldColumns = isSmallLayout ? 2 : 3;

  return (
    <ModalContainer
      navigationHeaderProps={{
        onBackPress: navigation.goBack,
        showBackButton: isSmallLayout,
      }}
    >
      <View style={styles.screenWrapper}>
        <KeyboardAvoidingView
          behavior={Platform.select({ android: undefined, ios: 'position' })}
          style={styles.base}
        >
          {loading ? (
            <ActivityIndicator style={styles.loadingContainer} />
          ) : Boolean(
              isOfflineModeActive &&
                offlineVisibleVariantPack &&
                offlineVariantPackPrices,
            ) || templateData ? (
            <List
              columnWrapperStyle={styles.previewColumn}
              contentContainerStyle={styles.previewList}
              data={previewFields}
              ItemSeparatorComponent={null}
              ListFooterComponent={
                <>
                  {retailPriceEnabled && priceHydrated ? (
                    <View style={styles.customPriceContainer}>
                      <TagPrintingTemplateCustomPriceField
                        availablePriceTypes={getAvailablePriceTypes(
                          templateMetadata.features,
                        )}
                        customPriceInMicros={customPriceInMicros}
                        offlinePrices={offlinePrices}
                        setCustomPriceInMicros={setCustomPriceInMicros}
                        variantPackUUID={variantPackUUID}
                      />
                    </View>
                  ) : null}
                </>
              }
              ListHeaderComponent={
                <>
                  <View style={styles.imageContainer}>
                    <OrderableItemListItemImage
                      alt={`image-${displayName}`}
                      hidePlaceholderImages={hidePlaceholderImages}
                      imageURLs={imageURLs}
                      listDisplaySize={ListDisplaySize.Huge}
                    />
                  </View>
                  <Text style={TYPOGRAPHY.h2}>{displayName}</Text>
                </>
              }
              listItemStyle={styles.listItem}
              numColumns={maxPreviewFieldColumns}
              renderMainContent={(item) => {
                return (
                  <View style={styles.previewContent}>
                    <Text style={[FONTS.primarySemiBold, styles.previewHeader]}>
                      {item.title}
                    </Text>
                    <Text style={[TYPOGRAPHY.p, styles.capitalizedText]}>
                      {he.decode(item.value)}
                    </Text>
                  </View>
                );
              }}
            />
          ) : null}
        </KeyboardAvoidingView>
        {isInstantPrint ? (
          <Button
            onPress={() => onPrintAgain(variantPackUUID, customPriceInMicros)}
            trackingData={{
              name: 'tag_print_batch_item_screen.print_again_button',
              properties: {
                printTagBatchUUID,
                variantPackUUID,
              },
            }}
          >
            {i18n.t('instant_print.print_again')}
          </Button>
        ) : null}
      </View>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    gap: 16,
  },
  capitalizedText: {
    textTransform: 'capitalize',
  },
  customPriceContainer: {
    marginBottom: 16,
  },
  imageContainer: {
    margin: 32,
  },
  listItem: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  loadingContainer: {
    marginTop: 24,
  },
  previewColumn: {
    gap: 8,
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  previewContent: {
    gap: 4,
  },
  previewHeader: {
    color: theme.sk.color.text.secondary,
    fontSize: 14,
  },
  previewList: {
    marginTop: 16,
  },
  screenWrapper: {
    flex: 1,
    paddingBottom: SCREEN_PADDING,
    paddingHorizontal: SCREEN_PADDING,
  },
}));
